<template>
	<DjCard
		v-if="!loading"
		class="bg-white cursor-pointer dj-card-company h-full"
		v-bind:class="{
			'szmd-highlight': szmdHighlight,
			'szmd-sponsor': szmdSponsor,
		}"
		hover
		@click="$emit('onClick', company.slug)"
	>
		<div class="absolute left-10 top-5 flex justify-left items-left max-w-[200px] z-10 flex-wrap">
			<BaseTagPill
				v-if="company.badges && company.badges.find((e: { slug?: string }) => e.slug == 'startup-ceg-vagyunk')"
				class="mr-1 mt-2"
				type="red"
				size="medium"
			>
				startup
			</BaseTagPill>
			<BaseTagPill v-if="company.has_gold" class="mr-1 mt-2" type="gold" size="medium">
				{{ langs.card.has_gold[locale] }}
			</BaseTagPill>
			<BaseTagPill v-if="szmdHighlight" class="mt-2 mr-1" type="red" size="medium">
				{{ $t("szmdShort") }}
			</BaseTagPill>
			<BaseTagPill v-if="szmdSponsor" class="mt-2 mr-1" type="red" size="medium">
				{{ $t("szmdSponsorShort") }}
			</BaseTagPill>
		</div>
		<div class="absolute right-10 top-5 flex justify-center items-center z-10" @click.stop>
			<DjCardLikeCompany @toggle-saved="toggleSaved" :isSaved="isSaved" :langs="langs" :locale="locale" />
		</div>
		<div class="relative overflow-hidden rounded-t-normal h-56 bg-dj-grey-light dj-image-header">
			<BaseCloudImage
				v-if="company.list_profile_image"
				custom-class="overflow-hidden rounded-t-normal h-56 object-cover"
				:public-id="company.list_profile_image.cloudinary"
				:alt="company.name"
				:transformation="[{ width: 640, height: 448, crop: 'fill' }]"
			/>
		</div>
		<div class="flex justify-between items-end ml-10 mr-5">
			<BaseCloudImage
				v-if="company.logo"
				custom-class="relative overflow-hidden rounded-normal border border-dg-grey-light w-dj-80 h-dj-80 -mt-10 z-10"
				:public-id="company.logo.cloudinary"
				:alt="company.name"
				:transformation="[{ width: 156, height: 156, crop: 'fill' }]"
			/>
			<div v-else class="relative rounded-normal w-dj-80 h-dj-80 -mt-10 z-10 bg-dj-red"></div>
			<BaseTagPill type="grey" size="small" v-if="company.jobs_count && company.jobs_count > 0">
				{{ company.jobs_count }} {{ langs.card.jobs[locale] }}
			</BaseTagPill>
		</div>
		<div class="flex flex-col mt-5 mr-5 ml-10">
			<DjClampedTitle :title="company.name" class="h-11 my-auto flex items-center" :clamp="2" />
			<DjClampedSubtitle
				v-if="company.boxes && company.boxes.mission && company.boxes.mission.data.motto[locale]"
				:subtitle="`&quot;${company.boxes.mission.data.motto[locale]}&quot;`"
				class="h-11 mt-3 flex items-center"
				:clamp="2"
			/>
			<span v-else>&nbsp;</span>
		</div>
		<div class="mt-3 mr-10 ml-10 mb-7">
			<DjCardCompanyTags :tags="sortTags()" :visible-tags="5" :lang="locale" />
		</div>
	</DjCard>
	<!-- Loading state -->
	<DjCard v-else class="bg-white border-dj-light-blue animate-pulse">
		<div class="absolute left-10 top-5 flex justify-center items-center z-10">
			<div class="w-20 h-7 mb-1 my-auto flex rounded-full bg-dj-light-blue"></div>
		</div>
		<div class="absolute right-10 top-5 flex justify-center items-center z-10">
			<div class="w-8 h-8 flex rounded-full bg-dj-light-blue"></div>
		</div>
		<div class="relative overflow-hidden rounded-t-normal h-56 bg-dj-grey-dark/10">
			<div
				class="w-full h-7 absolute bottom-0 left-0 right-0 text-right text-white text-xxs bg-dj-gray-minimal"
			></div>
		</div>
		<div class="flex justify-between items-end ml-10 mr-7">
			<div
				class="relative overflow-hidden rounded-normal border border-transparent bg-dj-gray-minimal w-dj-80 h-dj-80 -mt-10 z-10"
			></div>
			<div class="w-16 h-6 mt-2 mb-1 my-auto flex rounded-full bg-dj-gray-minimal"></div>
		</div>
		<div class="flex flex-col mt-5 mr-7 ml-10">
			<div class="w-full h-5 mb-1 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			<div class="w-full h-5 my-auto flex rounded-lg bg-dj-gray-minimal"></div>

			<div class="w-full h-4 mt-4 mb-1 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			<div class="w-full h-4 mb-2 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
		</div>
		<div class="mt-4 mr-10 ml-10 mb-7">
			<div class="w-full h-3 mb-2 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
			<div class="w-full h-3 my-auto flex rounded-lg bg-dj-gray-minimal"></div>
		</div>
	</DjCard>
</template>

<script lang="ts">
import BaseTagPill from "~/ui-kit/components/BaseTagPill.vue";
import BaseCloudImage from "~/ui-kit/components/BaseCloudImage.vue";

import DjCard from "./DjCard.vue";
import DjCardCompanyTags from "./partials/DjCardCompanyTags.vue";
import DjCardLikeCompany from "./partials/DjCardLikeCompany.vue";
import DjClampedTitle from "./partials/DjClampedTitle.vue";
import DjClampedSubtitle from "./partials/DjClampedSubtitle.vue";

import { ref, defineComponent, type PropType } from "vue";

import type { JobCardsLang } from "~/ui-kit/types/types";

export default defineComponent({
	name: "DjCardCompany",
	components: {
		BaseTagPill,
		BaseCloudImage,
		DjCard,
		DjCardCompanyTags,
		DjCardLikeCompany,
		DjClampedTitle,
		DjClampedSubtitle,
	},
	props: {
		company: {
			type: Object,
			default: () => {
				return {};
			},
		},
		locale: {
			type: String,
			default: "hu",
		},
		langs: {
			type: Object as PropType<JobCardsLang>,
			default: () => {
				return {};
			},
		},
		loading: {
			type: Boolean,
			default: false,
		},
		isSaved: {
			type: Boolean,
			default: false,
		},
	},
	emits: ["toggleSaved", "onClick"],
	setup(props, { emit }) {
		const szmdHighlight = ref(false);
		const szmdSponsor = ref(false);
		const locale = props.locale;

		szmdHighlight.value = props.company.szmd_status?.includes("top");

		szmdSponsor.value = props.company.szmd_sponsor_type;

		function toggleSaved() {
			emit("toggleSaved");
		}

		function sortTags() {
			const pattern = { industry: 1, company_size: 2, location: 3, country: 4 };

			if (typeof props.company.tags === "undefined") {
				return {};
			}

			return Object.keys(props.company.tags)
				.sort((x, y) => {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					//@ts-ignore
					return pattern[x] - pattern[y];
				})
				.reduce(
					(acc, key) => ({
						...acc,
						[key]: props.company.tags[key],
					}),
					{}
				);
		}

		return {
			locale,
			sortTags,
			toggleSaved,
			szmdHighlight,
			szmdSponsor,
		};
	},
});
</script>
<style lang="css">
.dj-card-company.szmd-highlight,
.dj-card-company.szmd-sponsor {
	border-width: 0px;
	outline: 2px solid #f17e90;
	outline-offset: -2px;
}

.dj-card-company.szmd-sponsor {
	outline-width: 1px;
	outline-offset: -1px;
}

.dj-card-company .dj-image-header,
.dj-card-company .dj-image-header > div {
	border-radius: 20px 20px 0 0;
}
</style>
